/**
 * Voice Socket Native Service - React Native Audio Support for Voice Mode
 * 
 * Provides native audio recording and playback using expo-av for Android/iOS.
 * Used by voiceSocket.js when Platform.OS !== 'web'.
 * 
 * Features:
 * - Audio recording using expo-av Audio.Recording
 * - Audio playback using expo-av Audio.Sound
 * - Time-based silence detection (VAD)
 * - Socket.IO integration for real-time voice AI
 */

import { Platform, AppState } from 'react-native';
import { Audio } from 'expo-av';

// Hardcoded interruption-mode values — expo-av's named exports
// (InterruptionModeIOS, InterruptionModeAndroid) can be undefined on native
// depending on the Metro bundler version.
//
// Values sourced from expo-av source:
//   InterruptionModeIOS:     MixWithOthers=0, DoNotMix=1, DuckOthers=2
//   InterruptionModeAndroid: DoNotMix=1, DuckOthers=2
const INTERRUPTION_MODE_IOS_DUCK_OTHERS = 2;
const INTERRUPTION_MODE_ANDROID_DUCK_OTHERS = 2;
import * as FileSystem from 'expo-file-system';

// ──────────────────────────────────────────────────────────────────────────────
// Recording configuration optimized for speech.
//
// Uses HARDCODED numeric values instead of named constants because expo-av's
// named exports are unreliable on native.
//
// IMPORTANT: linearPCM* options MUST be included even for AAC recording —
// expo-av's native iOS code always reads these fields and passes them to
// AVAudioRecorder's settings dictionary.  If they're omitted, the native
// side may construct an invalid settings dict causing prepareToRecord() to
// fail with "recorder not prepared".
// ──────────────────────────────────────────────────────────────────────────────
const RECORDING_OPTIONS = {
  isMeteringEnabled: true,
  android: {
    extension: '.m4a',
    outputFormat: 2,    // AndroidOutputFormat.MPEG_4
    audioEncoder: 3,    // AndroidAudioEncoder.AAC
    sampleRate: 44100,
    numberOfChannels: 1,
    bitRate: 128000,
  },
  ios: {
    extension: '.m4a',
    outputFormat: 1633772320, // IOSOutputFormat.MPEG4AAC ('aac ')
    audioQuality: 96,         // IOSAudioQuality.HIGH
    sampleRate: 44100,         // 44.1 kHz — safe default that all iOS devices support
    numberOfChannels: 1,
    bitRate: 128000,
    linearPCMBitDepth: 16,
    linearPCMIsBigEndian: false,
    linearPCMIsFloat: false,
  },
  web: {
    mimeType: 'audio/webm',
    bitsPerSecond: 128000,
  },
};

// VAD (Voice Activity Detection) settings
const SILENCE_THRESHOLD_MS = 1800; // Default stop recording after 1.8s of silence
const MAX_RECORDING_DURATION_MS = 30000; // Max 30 seconds per utterance
const MIN_RECORDING_DURATION_MS = 400; // Min 0.4 seconds to be valid (was 0.5s)

/**
 * Native Audio Manager for Voice Mode
 * Handles recording and playback on React Native
 */
class NativeAudioManager {
  constructor() {
    // Recording state
    this.recording = null;
    this.recordingUri = null;
    this.isRecording = false;
    
    // Playback state
    this.soundObject = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.isPlaybackPending = false;
    
    // VAD/silence detection
    this.recordingStartTime = null;
    this.silenceTimeout = null;
    this.maxDurationTimeout = null;
    this.meteringInterval = null;
    this.lastSoundTime = null;
    
    // Callbacks
    this.onUtteranceEnd = null;
    this.onPlaybackComplete = null;
    this.onError = null;
    
    // Mute state
    this.isMuted = false;
    
    // Audio level (exposed for UI visualization)
    this._currentMeteringDb = -160;
    this._recoveryAttempted = false;
    this._recoveryAttemptTime = 0;
    this._meteringUndefinedSince = null;
    this._meteringUndefinedCount = 0;
    this._dropCounters = {
      tooShort: 0,
      fileTooSmall: 0,
    };
  }

  /**
   * Initialize audio mode for recording.
   *
   * Uses console.log/console.error (NOT devLog) so diagnostic output is
   * visible in preview / production builds where devLog is silenced.
   *
   * @param {Object} options
   * @param {boolean} options.skipPermissionCheck - If true, skip the permission
   *   request (caller already ensured permission is granted). This avoids
   *   triple-requesting on iOS which can cause the system dialog to misfire.
   */
  async initializeAudio({ skipPermissionCheck = false } = {}) {
    try {
      // ── Step 1: Verify expo-av Audio module is loaded ──
      if (!Audio || typeof Audio.requestPermissionsAsync !== 'function') {
        throw new Error('expo-av Audio module is not available');
      }
      console.log('[NativeAudio] expo-av Audio module OK');

      // ── Step 2: Request microphone permission ──
      // Skip if caller (e.g. VoiceModeOverlay) already verified permission.
      // Calling requestPermissionsAsync() multiple times on iOS can cause
      // the system dialog to not appear or return stale cached results.
      if (skipPermissionCheck) {
        console.log('[NativeAudio] Skipping permission check (caller already verified)');
      } else {
        console.log('[NativeAudio] Checking microphone permission...');
        const currentPermission = await Audio.getPermissionsAsync();
        console.log('[NativeAudio] Current permission status:', JSON.stringify(currentPermission));

        if (currentPermission?.status !== 'granted') {
          console.log('[NativeAudio] Requesting microphone permission...');
          const permissionResult = await Audio.requestPermissionsAsync();
          console.log('[NativeAudio] Permission result:', JSON.stringify(permissionResult));

          if (permissionResult.status !== 'granted') {
            if (!permissionResult.canAskAgain) {
              throw new Error('MICROPHONE_PERMANENTLY_DENIED');
            }
            throw new Error('MICROPHONE_DENIED');
          }
        } else {
          console.log('[NativeAudio] Microphone permission already granted');
        }
      }

      // ── Step 3: Configure audio session for recording ──
      // Wait for app to be fully in the foreground before activating the audio session.
      // iOS refuses to activate if the app isn't active, causing
      // "This experience is currently in the background" errors.
      if (Platform.OS === 'ios' && AppState.currentState !== 'active') {
        console.log('[NativeAudio] App not in foreground (state:', AppState.currentState, '), waiting...');
        await new Promise((resolve) => {
          const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
              subscription.remove();
              resolve();
            }
          });
          setTimeout(() => { subscription.remove(); resolve(); }, 5000);
        });
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Configure audio session with retry. iOS can transiently fail to
      // activate the audio session during app state transitions.
      console.log('[NativeAudio] Setting audio mode (allowsRecordingIOS: true)...');
      let audioModeSet = false;
      for (let attempt = 0; attempt < 3 && !audioModeSet; attempt++) {
        try {
          await Audio.setAudioModeAsync({
            allowsRecordingIOS: true,
            playsInSilentModeIOS: true,
            staysActiveInBackground: false,
            shouldDuckAndroid: true,
            playThroughEarpieceAndroid: false,
            interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
            interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
          });
          audioModeSet = true;
          console.log('[NativeAudio] Audio mode set successfully (attempt', attempt + 1, ')');
        } catch (audioModeErr) {
          console.error(`[NativeAudio] setAudioModeAsync attempt ${attempt + 1}/3 failed:`, audioModeErr.message);
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          }
        }
      }
      if (!audioModeSet) {
        throw new Error('Failed to activate audio session. Please make sure the app is in the foreground and try again.');
      }

      // ── Step 4: Verify recording actually works with a quick test ──
      // Only run the smoke test when we performed the permission check ourselves.
      // When skipPermissionCheck is true (voice mode flow), the caller already
      // verified permission AND pre-configured the audio session.  Running the
      // smoke test in that case creates/destroys an AVAudioRecorder which can
      // leave the native audio session in a bad state — the subsequent real
      // startRecording() call then fails with "recorder not prepared" on iOS.
      if (!skipPermissionCheck) {
        console.log('[NativeAudio] Running recording smoke test...');
        try {
          const testResult = await Audio.Recording.createAsync(RECORDING_OPTIONS);
          if (testResult?.recording) {
            await testResult.recording.stopAndUnloadAsync();
            // Clean up the test file
            const testUri = testResult.recording.getURI();
            if (testUri) {
              FileSystem.deleteAsync(testUri, { idempotent: true }).catch(() => {});
            }
            console.log('[NativeAudio] Recording smoke test PASSED');
          }
        } catch (testErr) {
          console.error('[NativeAudio] Recording smoke test FAILED:', testErr.message);
          const errMsg = (testErr.message || '').toLowerCase();
          if (errMsg.includes('permission') || errMsg.includes('not authorized') || errMsg.includes('denied')) {
            console.error('[NativeAudio] The user should go to Settings > Privacy & Security > Microphone and enable access for this app.');
            throw new Error('MICROPHONE_PERMANENTLY_DENIED');
          }
          console.warn('[NativeAudio] Smoke test failed with non-permission error, continuing anyway:', testErr.message);
        }

        // Small delay to let native resources fully release after the test
        await new Promise(resolve => setTimeout(resolve, 300));
      } else {
        console.log('[NativeAudio] Skipping smoke test (permission already verified by caller)');
      }

      console.log('[NativeAudio] Audio initialized successfully');
      return true;
    } catch (error) {
      console.error('[NativeAudio] initializeAudio FAILED:', error.message);
      this.onError?.(error.message);
      throw error;
    }
  }

  /**
   * Start recording audio
   * @param {Function} onUtteranceEnd - Called when utterance ends with base64 audio
   * @param {Object} options
   * @param {boolean} options.skipAudioModeSetup - Skip setAudioModeAsync if caller
   *   already configured it (e.g. initializeAudio just ran). This avoids iOS audio
   *   session thrashing where repeated activate/deactivate cycles break the mic route.
   * @param {boolean} options.resetRecoveryAttempt - Reset one-shot recovery guard for
   *   fresh user-initiated recordings.
   */
  async startRecording(onUtteranceEnd, { skipAudioModeSetup = false, resetRecoveryAttempt = true } = {}) {
    if (this.isRecording) {
      console.log('[NativeAudio] Already recording');
      return true;
    }

    this.onUtteranceEnd = onUtteranceEnd;

    try {
      if (Platform.OS === 'ios' && AppState.currentState !== 'active') {
        console.log('[NativeAudio] startRecording: app not active, waiting for foreground...');
        await new Promise((resolve) => {
          const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
              subscription.remove();
              resolve();
            }
          });
          setTimeout(() => { subscription.remove(); resolve(); }, 5000);
        });
      }

      // Make sure previous recording is fully cleaned up
      await this.cleanupRecording();

      // Give expo-av's native bridge time to fully release the previous recorder.
      // iOS needs more time than Android to tear down AVAudioRecorder resources.
      await new Promise(resolve => setTimeout(resolve, Platform.OS === 'ios' ? 350 : 200));

      if (skipAudioModeSetup) {
        console.log('[NativeAudio] Skipping audio mode setup (caller already configured)');
      } else {
        // Ensure audio session is in recording mode before every new recording.
        // Use retry logic since iOS can fail transiently during app state transitions.
        let recordAudioModeSet = false;
        for (let attempt = 0; attempt < 3 && !recordAudioModeSet; attempt++) {
          try {
            await Audio.setAudioModeAsync({
              allowsRecordingIOS: true,
              playsInSilentModeIOS: true,
              staysActiveInBackground: false,
              shouldDuckAndroid: true,
              playThroughEarpieceAndroid: false,
              interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
              interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
            });
            recordAudioModeSet = true;
          } catch (modeErr) {
            console.warn(`[NativeAudio] startRecording setAudioModeAsync attempt ${attempt + 1}/3 failed:`, modeErr.message);
            if (attempt < 2) {
              await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
            }
          }
        }
        if (!recordAudioModeSet) {
          console.error('[NativeAudio] Failed to set audio mode for recording after retries');
          // Continue anyway — the mode may already be correct from initializeAudio
        }
      }

      // Brief pause after configuring audio session to let iOS settle.
      if (Platform.OS === 'ios' && !skipAudioModeSetup) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Retry logic — expo-av can transiently fail with "recorder not prepared"
      // if the native audio session isn't fully ready.
      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [600, 1200, 2000]; // ms — iOS needs generous delays
      let recording = null;
      let lastError = null;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          console.log(`[NativeAudio] createAsync attempt ${attempt + 1}/${MAX_RETRIES}...`);
          const result = await Audio.Recording.createAsync(
            RECORDING_OPTIONS,
            this._onRecordingStatusUpdate.bind(this),
            100 // Status update interval in ms
          );
          recording = result.recording;
          console.log('[NativeAudio] createAsync succeeded');
          break;
        } catch (err) {
          lastError = err;
          console.warn(`[NativeAudio] createAsync attempt ${attempt + 1}/${MAX_RETRIES} failed:`, err.message);

          if (attempt < MAX_RETRIES - 1) {
            // Clean up any partially-created recorder before retrying
            try {
              if (this.recording) {
                await this.recording.stopAndUnloadAsync();
                this.recording = null;
              }
            } catch (_) { /* ignore */ }

            // On iOS, fully reset the audio session before retrying.
            // Toggle allowsRecordingIOS off then on to force iOS to release
            // the previous AVAudioRecorder and create a fresh audio session.
            if (Platform.OS === 'ios') {
              try {
                await Audio.setAudioModeAsync({
                  allowsRecordingIOS: false,
                  playsInSilentModeIOS: true,
                  staysActiveInBackground: false,
                });
                await new Promise(resolve => setTimeout(resolve, 100));
              } catch (_) { /* ignore */ }
            }

            // Re-set audio mode for recording before retry
            try {
              await Audio.setAudioModeAsync({
                allowsRecordingIOS: true,
                playsInSilentModeIOS: true,
                staysActiveInBackground: false,
                shouldDuckAndroid: true,
                playThroughEarpieceAndroid: false,
                interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
                interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
              });
            } catch (_) { /* ignore */ }

            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          }
        }
      }

      if (!recording) {
        throw lastError || new Error('Failed to create recording after retries');
      }

      this.recording = recording;
      this.isRecording = true;
      this.recordingStartTime = Date.now();
      this.lastSoundTime = Date.now();

      // Reset metering stats for broken-mic detection
      this._meteringStats = null;
      this._meteringLogCounter = 0;
      this._meteringUndefinedSince = null;
      this._meteringUndefinedCount = 0;
      if (resetRecoveryAttempt) {
        this._recoveryAttempted = false;
      }

      // Set up silence detection
      this._startSilenceDetection();

      // Set up max duration timeout
      this.maxDurationTimeout = setTimeout(() => {
        console.log('[NativeAudio] Max duration reached');
        this.endUtterance();
      }, MAX_RECORDING_DURATION_MS);

      console.log('[NativeAudio] Recording started successfully');
      return true;
    } catch (error) {
      console.error('[NativeAudio] Failed to start recording:', error.message);
      if (error?.message?.includes?.('MICROPHONE_PERMANENTLY_DENIED')) {
        throw new Error('MICROPHONE_PERMANENTLY_DENIED');
      }
      if (
        error?.message?.includes?.('MICROPHONE_DENIED') ||
        error?.message?.toLowerCase?.().includes('permission denied') ||
        error?.message?.toLowerCase?.().includes('microphone')
      ) {
        throw new Error('MICROPHONE_DENIED');
      }
      throw new Error(`Recording failed: ${error.message}`);
    }
  }

  /**
   * Handle recording status updates (for metering/VAD)
   */
  _onRecordingStatusUpdate(status) {
    if (!status.isRecording || this.isMuted) return;

    const hasMetering = typeof status.metering === 'number';
    const metering = hasMetering ? status.metering : -160;
    const now = Date.now();
    this._currentMeteringDb = metering;

    if (Platform.OS === 'ios' && !hasMetering) {
      this._meteringUndefinedCount++;
      if (!this._meteringUndefinedSince) {
        this._meteringUndefinedSince = now;
      }

      // On some iOS builds metering is intermittently unavailable.
      // Keep the utterance open for the first 8 seconds so speech is not cut
      // off mid-sentence. After 8 seconds the silence detection's
      // metering-unavailable branch will trigger the utterance end
      // (see _startSilenceDetection).
      const recordingDuration = now - (this.recordingStartTime || now);
      if (recordingDuration < 8000) {
        this.lastSoundTime = now;
      }
      // After 8s we stop bumping lastSoundTime so silenceThresholdMs can expire
    } else if (hasMetering) {
      this._meteringUndefinedSince = null;
      this._meteringUndefinedCount = 0;
    }
    
    // Track metering stats to detect broken mic (all silence)
    if (!this._meteringStats) {
      this._meteringStats = { count: 0, maxDb: -160, aboveThresholdCount: 0 };
    }
    this._meteringStats.count++;
    if (metering > this._meteringStats.maxDb) {
      this._meteringStats.maxDb = metering;
    }
    
    // Metering thresholds for speech detection.
    // iOS metering values are in dBFS where 0 = max and -160 = silence.
    // Normal speech at arm's length is typically -45 to -55 dBFS on iOS.
    // Using -50 as the threshold — this captures normal and slightly quiet
    // speech while ignoring ambient noise and breathing that sits below -55.
    // A too-low threshold (e.g. -70) causes ambient noise to constantly
    // reset the silence timer, making end-of-utterance detection very slow.
    // Android metering uses a different scale, so -35 works well there.
    const threshold = Platform.OS === 'android' ? -35 : -50;
    if (metering > threshold) {
      this.lastSoundTime = Date.now();
      this._meteringStats.aboveThresholdCount++;
    }

    // Periodic metering log (every ~2s at 100ms interval) for on-device debugging
    if (!this._meteringLogCounter) this._meteringLogCounter = 0;
    this._meteringLogCounter++;
    if (this._meteringLogCounter % 20 === 0) {
      console.log(`[NativeAudio] metering: ${metering.toFixed(1)} dBFS, threshold: ${threshold}, lastSoundAge: ${Date.now() - (this.lastSoundTime || Date.now())}ms, maxDb: ${this._meteringStats.maxDb.toFixed(1)}, aboveThreshold: ${this._meteringStats.aboveThresholdCount}/${this._meteringStats.count}, meteringUndefinedCount: ${this._meteringUndefinedCount}`);
      if (!hasMetering && Platform.OS === 'ios') {
        console.warn('[NativeAudio] iOS metering is undefined; using fallback -160 dBFS');
      }
    }

    // After 30 status updates (~3 seconds), check if we ever got non-silence.
    // If all metering is at -160 dBFS, the microphone is likely not connected
    // to the audio session — this indicates audio session thrashing or a broken route.
    const hasConsistentUndefinedMetering =
      Platform.OS === 'ios' &&
      this._meteringUndefinedCount >= 20 &&
      this._meteringUndefinedSince &&
      (now - this._meteringUndefinedSince) >= 2000;

    if (
      (this._meteringStats.count >= 20 && this._meteringStats.maxDb <= -155) ||
      hasConsistentUndefinedMetering
    ) {
      console.error('[NativeAudio] WARNING: ~2 seconds of recording with no audio input detected (maxDb:', this._meteringStats.maxDb.toFixed(1), '). Microphone may not be connected. Attempting audio session reset...');
      // Try to fix by re-setting the audio mode
      this._attemptAudioSessionRecovery();
    }
  }

  /**
   * Attempt to recover a broken audio session by stopping, re-configuring, and restarting.
   */
  async _attemptAudioSessionRecovery() {
    if (this._recoveryAttempted) {
      const sinceLastAttempt = Date.now() - (this._recoveryAttemptTime || 0);
      if (sinceLastAttempt < 10000) {
        return;
      }
      console.log('[NativeAudio] Retrying audio session recovery after cooldown');
    }
    this._recoveryAttempted = true;
    this._recoveryAttemptTime = Date.now();

    console.log('[NativeAudio] Attempting audio session recovery...');
    const savedCallback = this.onUtteranceEnd;

    try {
      // Stop current recording
      if (this.recording) {
        try { await this.recording.stopAndUnloadAsync(); } catch (_) {}
        this.recording = null;
      }
      this.isRecording = false;

      // Clear timers
      if (this.meteringInterval) { clearInterval(this.meteringInterval); this.meteringInterval = null; }
      if (this.maxDurationTimeout) { clearTimeout(this.maxDurationTimeout); this.maxDurationTimeout = null; }

      // Toggle audio session off then on to force iOS to reconnect the mic route
      await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true });
      await new Promise(resolve => setTimeout(resolve, 300));
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
        staysActiveInBackground: false,
        shouldDuckAndroid: true,
        playThroughEarpieceAndroid: false,
        interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
        interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
      });
      await new Promise(resolve => setTimeout(resolve, 300));

      // Reset metering stats
      this._meteringStats = null;
      this._meteringLogCounter = 0;

      // Restart recording
      if (savedCallback) {
        console.log('[NativeAudio] Recovery: restarting recording...');
        await this.startRecording(savedCallback, { skipAudioModeSetup: true, resetRecoveryAttempt: false });
        console.log('[NativeAudio] Recovery: recording restarted successfully');
      }
    } catch (err) {
      console.error('[NativeAudio] Audio session recovery failed:', err.message);
    }
  }

  /**
   * Get normalized input volume (0-1) for UI visualization.
   */
  getInputVolume() {
    if (!this.isRecording || this.isMuted) return 0;
    const db = this._currentMeteringDb;
    const minDb = -60;
    const normalized = Math.max(0, Math.min(1, (db - minDb) / -minDb));
    return normalized;
  }

  /**
   * Start silence detection
   */
  _startSilenceDetection() {
    if (this.meteringInterval) {
      clearInterval(this.meteringInterval);
    }

    this.meteringInterval = setInterval(() => {
      if (!this.isRecording || this.isMuted) return;

      const now = Date.now();
      const silenceDuration = now - (this.lastSoundTime || now);
      const recordingDuration = now - (this.recordingStartTime || now);

      // When iOS metering is consistently undefined (no valid readings at all),
      // we cannot detect silence locally. Instead, use a fixed recording window
      // and let the server-side Whisper handle silence/speech segmentation.
      // The recording restarts immediately, so longer speech is split into
      // consecutive ~10s chunks — each transcribed independently.
      if (Platform.OS === 'ios' && this._meteringUndefinedSince &&
          this._meteringStats && this._meteringStats.aboveThresholdCount === 0 &&
          this._meteringUndefinedCount > 30) {
        // No metering available at all for this recording — use timer-based cutoff
        const noMeteringCutoffMs = 10000; // 10 seconds
        if (recordingDuration >= noMeteringCutoffMs) {
          console.log('[NativeAudio] No metering data available — timer-based utterance end', {
            recordingDuration,
            meteringUndefinedCount: this._meteringUndefinedCount,
          });
          this.endUtterance();
          return;
        }
      }

      let silenceThresholdMs = Platform.OS === 'ios' ? 700 : SILENCE_THRESHOLD_MS;
      if (Platform.OS === 'ios' && this._meteringUndefinedSince) {
        // Metering-unavailable fallback: allow extra time before splitting utterance.
        silenceThresholdMs = 1500;
      }
      if (recordingDuration >= MIN_RECORDING_DURATION_MS &&
          silenceDuration >= silenceThresholdMs) {
        console.log('[NativeAudio] Silence detected, ending utterance', {
          recordingDuration,
          silenceDuration,
          silenceThresholdMs,
          meteringUndefinedCount: this._meteringUndefinedCount,
        });
        this.endUtterance();
      }
    }, 100);
  }

  /**
   * End the current utterance and send audio
   */
  async endUtterance() {
    if (!this.isRecording || !this.recording) {
      return;
    }

    // Clear timeouts
    if (this.silenceTimeout) { clearTimeout(this.silenceTimeout); this.silenceTimeout = null; }
    if (this.maxDurationTimeout) { clearTimeout(this.maxDurationTimeout); this.maxDurationTimeout = null; }
    if (this.meteringInterval) { clearInterval(this.meteringInterval); this.meteringInterval = null; }

    try {
      await this.recording.stopAndUnloadAsync();
      const uri = this.recording.getURI();
      this.recordingUri = uri;
      this.isRecording = false;

      console.log('[NativeAudio] Recording stopped, URI:', uri);

      const recordingDuration = Date.now() - this.recordingStartTime;
      if (recordingDuration < MIN_RECORDING_DURATION_MS) {
        this._dropCounters.tooShort++;
        console.log('[NativeAudio] Recording too short, ignoring', {
          recordingDuration,
          threshold: MIN_RECORDING_DURATION_MS,
          dropCount: this._dropCounters.tooShort,
        });
        await this.cleanupRecording();
        if (!this.isRecording && !this.isPlaying && !this.isPlaybackPending && this.onUtteranceEnd) {
          try { await this.startRecording(this.onUtteranceEnd, { skipAudioModeSetup: true }); } catch (e) { console.warn('[NativeAudio] Restart failed:', e.message); }
        }
        return;
      }

      const fileInfo = await FileSystem.getInfoAsync(uri);
      if (!fileInfo.exists || fileInfo.size < 500) {
        this._dropCounters.fileTooSmall++;
        console.log('[NativeAudio] Recording file too small or missing', {
          exists: fileInfo.exists,
          size: fileInfo.size,
          minBytes: 500,
          dropCount: this._dropCounters.fileTooSmall,
        });
        await this.cleanupRecording();
        if (!this.isRecording && !this.isPlaying && !this.isPlaybackPending && this.onUtteranceEnd) {
          try { await this.startRecording(this.onUtteranceEnd, { skipAudioModeSetup: true }); } catch (e) { console.warn('[NativeAudio] Restart failed:', e.message); }
        }
        return;
      }

      const base64Audio = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      console.log('[NativeAudio] Sending audio', {
        size: fileInfo.size,
        recordingDuration,
        meteringUndefinedCount: this._meteringUndefinedCount,
      });

      const callback = this.onUtteranceEnd;
      if (callback) {
        // Use audio/mp4 which is the correct MIME type for AAC in M4A container
        // audio/m4a is NOT a valid MIME type and causes server-side processing failures
        callback(base64Audio, 'audio/mp4');
      }

      await this.cleanupRecording();
      if (!this.isRecording && !this.isPlaying && !this.isPlaybackPending && callback) {
        try { await this.startRecording(callback, { skipAudioModeSetup: true }); } catch (e) { console.warn('[NativeAudio] Restart failed:', e.message); }
      } else {
        console.log('[NativeAudio] Skipping recording restart — playback pending:', this.isPlaybackPending, 'playing:', this.isPlaying);
      }

    } catch (error) {
      console.warn('[NativeAudio] Error ending utterance:', error);
      const callback = this.onUtteranceEnd;
      await this.cleanupRecording();
      if (!this.isRecording && !this.isPlaying && !this.isPlaybackPending && callback) {
        try { await this.startRecording(callback, { skipAudioModeSetup: true }); } catch (e) { console.warn('[NativeAudio] Restart failed:', e.message); }
      }
    }
  }

  /**
   * Stop all recording
   */
  async stopRecording() {
    if (this.silenceTimeout) { clearTimeout(this.silenceTimeout); this.silenceTimeout = null; }
    if (this.maxDurationTimeout) { clearTimeout(this.maxDurationTimeout); this.maxDurationTimeout = null; }
    if (this.meteringInterval) { clearInterval(this.meteringInterval); this.meteringInterval = null; }

    this.isRecording = false;
    this.isPlaybackPending = false;
    this.onUtteranceEnd = null;

    await this.cleanupRecording();

    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        playsInSilentModeIOS: false,
        staysActiveInBackground: false,
        shouldDuckAndroid: false,
        playThroughEarpieceAndroid: false,
      });
    } catch (e) {
      console.warn('[NativeAudio] Failed to reset audio mode:', e);
    }

    console.log('[NativeAudio] Recording stopped');
  }

  /**
   * Clean up recording resources
   */
  async cleanupRecording() {
    if (this.recording) {
      try {
        await this.recording.stopAndUnloadAsync();
      } catch (e) {
        // Ignore - recording may already be stopped/unloaded
      }
      this.recording = null;
    }

    if (this.recordingUri) {
      try {
        await FileSystem.deleteAsync(this.recordingUri, { idempotent: true });
      } catch (e) {
        // Ignore
      }
      this.recordingUri = null;
    }
  }

  /**
   * Queue audio for playback
   */
  async queueAudio(base64Audio) {
    this.isPlaybackPending = true;
    
    if (this.isRecording) {
      console.log('[NativeAudio] Stopping recording for TTS playback');
      await this._stopRecordingForPlayback();
    }
    
    this.audioQueue.push(base64Audio);
    
    if (!this.isPlaying) {
      this._playNextInQueue();
    }
  }

  /**
   * Stop recording temporarily for playback
   */
  async _stopRecordingForPlayback() {
    if (this.silenceTimeout) { clearTimeout(this.silenceTimeout); this.silenceTimeout = null; }
    if (this.maxDurationTimeout) { clearTimeout(this.maxDurationTimeout); this.maxDurationTimeout = null; }
    if (this.meteringInterval) { clearInterval(this.meteringInterval); this.meteringInterval = null; }
    
    this.isRecording = false;
    
    if (this.recording) {
      try {
        const status = await this.recording.getStatusAsync();
        if (status.isRecording) {
          await this.recording.stopAndUnloadAsync();
        }
      } catch (e) {
        // Ignore
      }
      this.recording = null;
    }
    
    if (this.recordingUri) {
      try { await FileSystem.deleteAsync(this.recordingUri, { idempotent: true }); } catch (e) { }
      this.recordingUri = null;
    }
    
    console.log('[NativeAudio] Recording paused for playback');
  }

  /**
   * Play next audio in queue
   */
  async _playNextInQueue() {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      this.isPlaybackPending = false;
      
      if (this.onUtteranceEnd) {
        console.log('[NativeAudio] Playback queue empty — restarting recording');
        // NOTE: Don't call setAudioModeAsync here — startRecording() will handle it.
        // Calling it both here AND in startRecording causes iOS audio session thrashing.
        try {
          if (!this.isRecording) {
            await this.startRecording(this.onUtteranceEnd, { skipAudioModeSetup: true });
          }
        } catch (e) {
          console.warn('[NativeAudio] Failed to restart recording after playback:', e);
        }
      }
      
      this.onPlaybackComplete?.();
      return;
    }

    this.isPlaying = true;
    const base64Audio = this.audioQueue.shift();

    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        playsInSilentModeIOS: true,
        staysActiveInBackground: false,
        shouldDuckAndroid: true,
        playThroughEarpieceAndroid: false,
      }).catch(() => {});

      const tempUri = FileSystem.cacheDirectory + `tts_${Date.now()}.wav`;
      await FileSystem.writeAsStringAsync(tempUri, base64Audio, {
        encoding: FileSystem.EncodingType.Base64,
      });

      if (this.soundObject) {
        try { await this.soundObject.unloadAsync(); } catch (e) { }
        this.soundObject = null;
      }

      const { sound } = await Audio.Sound.createAsync(
        { uri: tempUri },
        { shouldPlay: true },
        this._onPlaybackStatusUpdate.bind(this)
      );

      this.soundObject = sound;

      setTimeout(() => {
        FileSystem.deleteAsync(tempUri, { idempotent: true }).catch(() => {});
      }, 10000);

    } catch (error) {
      console.warn('[NativeAudio] Playback error:', error);
      this._playNextInQueue();
    }
  }

  /**
   * Handle playback status updates
   */
  _onPlaybackStatusUpdate(status) {
    if (status.didJustFinish) {
      this._playNextInQueue();
    } else if (status.error) {
      console.warn('[NativeAudio] Playback status error:', status.error);
      this._playNextInQueue();
    }
  }

  /**
   * Stop all audio playback
   */
  async stopPlayback() {
    this.audioQueue = [];
    this.isPlaying = false;
    this.isPlaybackPending = false;

    if (this.soundObject) {
      try {
        await this.soundObject.stopAsync();
        await this.soundObject.unloadAsync();
      } catch (e) {
        // Ignore
      }
      this.soundObject = null;
    }

    console.log('[NativeAudio] Playback stopped');
  }

  setMuted(muted) {
    this.isMuted = muted;
  }

  static isSupported() {
    return Platform.OS === 'ios' || Platform.OS === 'android';
  }

  async cleanup() {
    await this.stopRecording();
    await this.stopPlayback();
  }
}

// Export singleton instance
const nativeAudioManager = new NativeAudioManager();
export default nativeAudioManager;
